// Cloudinary Configuration
// This file handles image uploads to Cloudinary

const CLOUDINARY_CLOUD_NAME = "dm5gxrp4o";
const CLOUDINARY_UPLOAD_PRESET = "nilkanth_art";
const CLOUDINARY_IMAGE_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

/**
 * Compress image before upload to save storage
 * @param {File} file - Original image file
 * @returns {Promise<Blob>} - Compressed image blob
 */
const compressImage = async (file) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Calculate new dimensions (max 1600px, maintains aspect ratio)
            let { width, height } = img;
            const maxSize = 1600;

            if (width > maxSize || height > maxSize) {
                if (width > height) {
                    height = (height / width) * maxSize;
                    width = maxSize;
                } else {
                    width = (width / height) * maxSize;
                    height = maxSize;
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with 80% quality (good balance of quality vs size)
            canvas.toBlob(
                (blob) => {
                    console.log(`Compressed: ${(file.size / 1024 / 1024).toFixed(2)} MB → ${(blob.size / 1024).toFixed(0)} KB`);
                    resolve(blob);
                },
                'image/jpeg',
                0.80 // 80% quality - visually identical, 70-90% smaller
            );
        };

        img.src = URL.createObjectURL(file);
    });
};

/**
 * Upload an image to Cloudinary (with compression)
 * @param {File|Blob} file - The image file to upload
 * @returns {Promise<string>} - The secure URL of the uploaded image
 */
export const uploadToCloudinary = async (file) => {
    // Compress image before upload to save storage
    const compressedBlob = await compressImage(file);

    const formData = new FormData();
    formData.append("file", compressedBlob, file.name || 'image.jpg');
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
        const response = await fetch(CLOUDINARY_IMAGE_UPLOAD_URL, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || "Upload failed");
        }

        const data = await response.json();
        return data.secure_url; // Return the HTTPS URL of the uploaded image
    } catch (error) {
        console.error("Cloudinary upload error:", error);
        throw error;
    }
};

/**
 * Upload multiple images to Cloudinary
 * @param {File[]} files - Array of image files to upload
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<string[]>} - Array of secure URLs
 */
export const uploadMultipleToCloudinary = async (files, onProgress) => {
    const urls = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await uploadToCloudinary(file);
        urls.push(url);

        if (onProgress) {
            onProgress(i + 1, files.length);
        }
    }

    return urls;
};

/**
 * Get optimized Cloudinary URL for frontend display
 * @param {string} url - Original Cloudinary URL
 * @param {object} options - Optimization options
 * @returns {string} - Optimized URL
 */
export const getOptimizedUrl = (url, options = {}) => {
    if (!url || !url.includes('cloudinary.com')) {
        return url; // Return as-is if not a Cloudinary URL
    }

    const {
        width = null,
        height = null,
        quality = 'auto',
        format = 'auto',
        crop = 'fill'
    } = options;

    // Build transformation string
    let transforms = [`q_${quality}`, `f_${format}`];
    if (width) transforms.push(`w_${width}`);
    if (height) transforms.push(`h_${height}`);
    if (width || height) transforms.push(`c_${crop}`);

    const transformStr = transforms.join(',');

    // Insert transformation into URL
    return url.replace('/upload/', `/upload/${transformStr}/`);
};

// Preset optimization functions for common use cases
export const getThumbUrl = (url) => getOptimizedUrl(url, { width: 100, height: 100 });
export const getListUrl = (url) => getOptimizedUrl(url, { width: 400 });
export const getDetailUrl = (url) => getOptimizedUrl(url, { width: 1200 });

export { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET };
